import { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  Box,
  Grid,
  Typography,
  Button,
  Container,
  Switch,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  Divider
} from "@mui/material";
import {
  faCircleCheck,
  faClockRotateLeft,
  faEye,
  faCheck
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { DefaultPaper } from "@aclymatepackages/atoms";
import { formatDecimal } from "@aclymatepackages/formatters";
import {
  findConvertedProductWeights,
  calculateProductFootprintBreakdown
} from "@aclymatepackages/products";
import { ProductFootprintPieChart } from "@aclymatepackages/modules";

import ProductDetailsSlider from "../../modules/products/ProductDetailsSlider";
import SharedReportOverlay from "../../modules/reporting/SharedReportOverlay";

import {
  useCachedDisplayData,
  useCachedFirebaseCrud,
  useCollectionDataListener
} from "../../../helpers/firebase";
import { shareProductBom } from "../../../helpers/utils/apiCalls";
import useProductsEmissionsFactors from "../../../helpers/contexts/ProductsEmissionsFactors";
import { isProductBomComplete } from "../../../helpers/components/products";
import usePermissions from "../../../helpers/hooks/usePermissions";

const ReceivedProductLeftContent = ({
  basicInfo,
  components,
  packaging,
  electronics,
  route,
  originalKgsCo2e,
  originalFootprintBreakdownKgs,
  onOpenReport
}) => {
  const [{ emissionsFactors }] = useProductsEmissionsFactors();

  const computedProduct = {
    ...basicInfo,
    ...findConvertedProductWeights(basicInfo),
    components,
    packaging,
    electronics,
    route
  };

  const { kgsCo2e: computedKgs, footprintBreakdownKgs: computedBreakdown } =
    calculateProductFootprintBreakdown(computedProduct, emissionsFactors);

  const hasStoredBreakdown =
    originalFootprintBreakdownKgs &&
    Object.keys(originalFootprintBreakdownKgs).length > 0;

  const kgsCo2e = originalKgsCo2e || computedKgs || 0;
  const footprintBreakdownKgs = hasStoredBreakdown
    ? originalFootprintBreakdownKgs
    : computedBreakdown || {};

  return (
    <Container maxWidth="sm">
      <DefaultPaper>
        <Grid container direction="column" spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="h4" align="center">
              Total Product Footprint: {formatDecimal(kgsCo2e)} kgs
            </Typography>
          </Grid>
          <Grid item>
            <ProductFootprintPieChart
              footprintBreakdownKgs={footprintBreakdownKgs}
              totalFootprintKgs={kgsCo2e}
            />
          </Grid>
          {onOpenReport && (
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                onClick={onOpenReport}
              >
                View Full Report
              </Button>
            </Grid>
          )}
        </Grid>
      </DefaultPaper>
    </Container>
  );
};

const statusChipMap = {
  reviewed: { label: "Reviewed", icon: faCircleCheck, color: "success" },
  shared: { label: "Pending Review", icon: faClockRotateLeft, color: "warning" }
};

const ShareTypeStatusRow = ({ label, isShared, status }) => {
  const chipConfig = isShared ? statusChipMap[status] : null;

  return (
    <Grid container alignItems="center" spacing={1}>
      <Grid item xs>
        <Typography variant="body2">{label}</Typography>
      </Grid>
      <Grid item>
        {chipConfig ? (
          <Chip
            size="small"
            label={chipConfig.label}
            color={chipConfig.color}
            icon={<FontAwesomeIcon icon={chipConfig.icon} />}
          />
        ) : (
          <Chip size="small" label="Not Shared" variant="outlined" />
        )}
      </Grid>
    </Grid>
  );
};

const SupplierTab = ({
  vendorName,
  isFinalized,
  sharedEntry,
  onMarkBomReviewed,
  onMarkReportReviewed,
  onViewSupplier
}) => {
  const { bomShared, reportShared, bomStatus, reportStatus } =
    sharedEntry || {};

  const canReviewBom = bomShared && bomStatus !== "reviewed";
  const canReviewReport = reportShared && reportStatus !== "reviewed";

  return (
    <Box p={3}>
      <Paper
        elevation={0}
        sx={{ bgcolor: "backgroundGray.main", p: 2, borderRadius: 2 }}
      >
        <Grid container direction="column" spacing={2}>
          <Grid
            item
            container
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Grid item xs>
              <Typography variant="subtitle1">{vendorName}</Typography>
              {!isFinalized && (
                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  NOTE: This product data is preliminary and may change.
                </Typography>
              )}
            </Grid>
            <Grid item>
              <Tooltip title="View Supplier">
                <IconButton size="small" onClick={onViewSupplier}>
                  <FontAwesomeIcon icon={faEye} />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container direction="column" spacing={1}>
              <Grid item>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs>
                    <ShareTypeStatusRow
                      label="BOM Data"
                      isShared={bomShared}
                      status={bomStatus}
                    />
                  </Grid>
                  {canReviewBom && (
                    <Grid item>
                      <Tooltip title="Mark as Reviewed">
                        <IconButton size="small" onClick={onMarkBomReviewed}>
                          <FontAwesomeIcon icon={faCheck} />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item>
                <Divider />
              </Grid>
              <Grid item>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs>
                    <ShareTypeStatusRow
                      label="Report"
                      isShared={reportShared}
                      status={reportStatus}
                    />
                  </Grid>
                  {canReviewReport && (
                    <Grid item>
                      <Tooltip title="Mark as Reviewed">
                        <IconButton size="small" onClick={onMarkReportReviewed}>
                          <FontAwesomeIcon icon={faCheck} />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

const ReceivedProductDetailsSlider = ({
  selectedProduct,
  setSelectedProduct
}) => {
  const history = useHistory();
  const location = useLocation();
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const [relationships] = useCachedDisplayData("vendor-relationships");

  const {
    name: productName,
    isFinalized,
    vendorName,
    sharedEntry,
    relationshipId,
    kgsCo2e: originalKgsCo2e,
    footprintBreakdownKgs: originalFootprintBreakdownKgs
  } = selectedProduct;
  const { bomShared, reportShared } = sharedEntry || {};

  const relationship = relationships.find((r) => r.id === relationshipId);
  const fullSharedProducts = relationship?.sharedProducts || [];

  const updateSharedEntry = (updates) => {
    const updatedSharedProducts = fullSharedProducts.map((p) =>
      p.productId === sharedEntry?.productId ? { ...p, ...updates } : p
    );
    updateCollectionDoc("vendor-relationships", relationshipId, {
      sharedProducts: updatedSharedProducts
    });
  };

  const onMarkBomReviewed = () => updateSharedEntry({ bomStatus: "reviewed" });
  const onMarkReportReviewed = () =>
    updateSharedEntry({ reportStatus: "reviewed" });

  const onViewSupplier = () => {
    const params = new URLSearchParams(location.search);
    params.set("selectedId", relationshipId);
    history.push({
      pathname: "/explorer/dashboard/suppliers",
      search: params.toString()
    });
  };

  const warningNote = !isFinalized
    ? "This product data is preliminary and may change."
    : null;

  const pendingNote = !bomShared
    ? "BOM data has not been shared. Contact your supplier to request it."
    : null;

  const supplierTab = {
    label: "Supplier",
    value: "supplier",
    content: (
      <SupplierTab
        vendorName={vendorName}
        isFinalized={isFinalized}
        sharedEntry={sharedEntry}
        onMarkBomReviewed={onMarkBomReviewed}
        onMarkReportReviewed={onMarkReportReviewed}
        onViewSupplier={onViewSupplier}
      />
    )
  };

  const [viewingReport, setViewingReport] = useState(false);
  const reportUrl = sharedEntry?.reportUrl;

  return (
    <>
      {viewingReport && reportUrl && (
        <SharedReportOverlay
          reportUrl={reportUrl}
          title={`${productName} Product Report`}
          isFinalized={isFinalized}
          onClose={() => setViewingReport(false)}
        />
      )}
      <ProductDetailsSlider
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
        additionalTabs={[supplierTab]}
        otherProducts={[]}
        isInApp
        readOnly
        pendingNote={pendingNote}
        warningNote={warningNote}
        footprintShared={reportShared}
        LeftContentComponent={
          reportShared && reportUrl ? ReceivedProductLeftContent : undefined
        }
        leftContentProps={{
          originalFootprintBreakdownKgs: originalFootprintBreakdownKgs || {},
          originalKgsCo2e: originalKgsCo2e || 0,
          onOpenReport: () => setViewingReport(true)
        }}
        lifecycleCollectionName="vendor-assigned-product-lifecycle-events"
        productCollectionName="product-components"
      />
    </>
  );
};

const CustomerProductRow = ({
  lifecycleEventId,
  buyerName,
  bomStatus,
  vendorId,
  updateCollectionDoc,
  hasProductData,
  canShare
}) => {
  const isShared = bomStatus === "confirmed";
  const isRequested = bomStatus === "requested" || isShared;

  const handleToggle = async () => {
    updateCollectionDoc(
      "vendor-assigned-product-lifecycle-events",
      lifecycleEventId,
      {
        bomStatus: isShared ? "requested" : "confirmed"
      }
    );
    await shareProductBom({ lifecycleEventId, vendorId, share: !isShared });
  };

  const statusMessages = {
    confirmed: `BOM data shared with ${buyerName}.`,
    requested: `${buyerName} has requested your BOM data.`
  };

  const defaultMessage = `Share your product data with ${buyerName}.`;
  const notRequestedMessage = `${buyerName} hasn't requested this product's BOM data yet.`;
  const missingDataMessage =
    "Fill in product specs and materials before sharing.";

  const rowMessage = !hasProductData
    ? missingDataMessage
    : !isRequested
    ? notRequestedMessage
    : statusMessages[bomStatus] || defaultMessage;

  const isToggleDisabled = !hasProductData || !canShare || !isRequested;

  const disabledTooltipMap = {
    missingData: missingDataMessage,
    noPermission: "You don't have permission to share product data.",
    notRequested: notRequestedMessage
  };
  const disabledReason = !hasProductData
    ? "missingData"
    : !canShare
    ? "noPermission"
    : !isRequested
    ? "notRequested"
    : null;
  const disabledTooltip = disabledReason ? disabledTooltipMap[disabledReason] : "";

  const toggleSwitch = (
    <Switch
      checked={isShared}
      onChange={handleToggle}
      color="primary"
      disabled={isToggleDisabled}
    />
  );

  return (
    <Grid container alignItems="center" spacing={2}>
      <Grid item xs>
        <Typography variant="subtitle2">{buyerName}</Typography>
        <Typography variant="body2" color="textSecondary">
          {rowMessage}
        </Typography>
      </Grid>
      <Grid item>
        {isToggleDisabled ? (
          <Tooltip title={disabledTooltip}>
            <span>{toggleSwitch}</span>
          </Tooltip>
        ) : (
          toggleSwitch
        )}
      </Grid>
    </Grid>
  );
};

const CustomersTab = ({ productId, hasProductData }) => {
  const vendorId = window.sessionStorage.getItem("accountId");
  const [productRequests] = useCachedDisplayData(
    "vendor-assigned-product-lifecycle-events"
  );
  const [customers] = useCollectionDataListener("customers");
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const { canShare } = usePermissions();

  const buyerNameMap = Object.fromEntries(
    customers.map(({ buyerId, buyerName }) => [buyerId, buyerName])
  );

  const customerRequests = productRequests.filter(
    (r) => r.productId === productId
  );

  if (!customerRequests.length) {
    return (
      <Box p={3}>
        <Typography variant="body2" color="textSecondary" align="center">
          No customers have requested BOM data for this product.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Grid container direction="column" spacing={2}>
        {customerRequests.map((request) => (
          <Grid item key={request.id}>
            <CustomerProductRow
              lifecycleEventId={request.id}
              buyerName={buyerNameMap[request.ownerId] || request.ownerId}
              bomStatus={request.bomStatus}
              vendorId={vendorId}
              updateCollectionDoc={updateCollectionDoc}
              hasProductData={hasProductData}
              canShare={canShare}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const OwnProductDetailsSlider = ({ selectedProduct, setSelectedProduct }) => {
  const [, emissionsFactorsLoading] = useProductsEmissionsFactors();
  const { canEdit } = usePermissions();

  if (emissionsFactorsLoading) {
    return null;
  }

  const onSaveSuccess = () => {
    setSelectedProduct(null);
  };

  const additionalTabs = [
    {
      label: "Customers",
      value: "customers",
      content: ({ editableProduct }) => (
        <CustomersTab
          productId={selectedProduct.id}
          hasProductData={isProductBomComplete(editableProduct)}
        />
      )
    }
  ];

  return (
    <ProductDetailsSlider
      selectedProduct={selectedProduct}
      setSelectedProduct={setSelectedProduct}
      additionalTabs={additionalTabs}
      otherProducts={[]}
      isInApp
      readOnly={!canEdit}
      disabled={!canEdit}
      onSaveSuccess={onSaveSuccess}
      lifecycleCollectionName="vendor-assigned-product-lifecycle-events"
      productCollectionName="vendor-assigned-products"
    />
  );
};

const ExplorerProductDetailsSlider = ({
  selectedProduct,
  setSelectedProduct
}) => {
  if (selectedProduct.isReceived) {
    return (
      <ReceivedProductDetailsSlider
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
      />
    );
  }

  return (
    <OwnProductDetailsSlider
      selectedProduct={selectedProduct}
      setSelectedProduct={setSelectedProduct}
    />
  );
};
export default ExplorerProductDetailsSlider;
